import React, { useState, useEffect } from 'react';
import { Animal } from '../types/Animal';
import { AnimalService } from '../services/animalService';
import AnimalCard from './AnimalCard';
import AnimalModal from './AnimalModal';
import './AnimalGallery.css';

const AnimalGallery: React.FC = () => {
  const [animals, setAnimals] = useState<Animal[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedAnimal, setSelectedAnimal] = useState<Animal | null>(null);
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    species: [] as string[],
    sex: [] as string[],
    tags: [] as string[]
  });
  const [showFilters, setShowFilters] = useState(false);
  const [showFavorites, setShowFavorites] = useState(false);
  const [favorites, setFavorites] = useState<string[]>([]);
  const [expandedFilters, setExpandedFilters] = useState({
    species: false,
    sex: false,
    features: false
  });

  useEffect(() => {
    const loadAnimals = async () => {
      try {
        const fetchedAnimals = await AnimalService.fetchAnimals();
        console.log('Loaded animals:', fetchedAnimals);
        setAnimals(fetchedAnimals);
      } catch (error) {
        console.error('Failed to load animals:', error);
      } finally {
        setLoading(false);
      }
    };

    loadAnimals();
  }, []);

  // Preload first images of all animals once data is loaded
  useEffect(() => {
    if (animals.length > 0) {
      // Extract first photo from each animal for immediate preloading
      const firstPhotos = animals
        .map(animal => animal.photos[0])
        .filter(Boolean);
      
      if (firstPhotos.length > 0) {
        console.log(`🚀 Preloading ${firstPhotos.length} first images...`);
        // Simple image preloading without context
        firstPhotos.forEach(photoUrl => {
          const img = new Image();
          img.src = photoUrl;
        });
      }
    }
  }, [animals]);

  // Load favorites from localStorage on component mount
  useEffect(() => {
    const savedFavorites = localStorage.getItem('animalFavorites');
    if (savedFavorites) {
      try {
        setFavorites(JSON.parse(savedFavorites));
      } catch (error) {
        console.error('Error loading favorites:', error);
      }
    }
  }, []);

  // Handle browser back button
  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      if (selectedAnimal && !event.state?.modal) {
        // User pressed back button while modal was open
        setSelectedAnimal(null);
        // Restore scroll position
        const savedPosition = sessionStorage.getItem('animalGalleryScrollPosition');
        if (savedPosition) {
          setTimeout(() => {
            window.scrollTo(0, parseInt(savedPosition, 10));
          }, 0);
        }
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [selectedAnimal]);

  // Save favorites to localStorage whenever favorites change
  useEffect(() => {
    localStorage.setItem('animalFavorites', JSON.stringify(favorites));
  }, [favorites]);

  const filteredAnimals = animals.filter(animal => {
    // If showing favorites only, filter by favorites first
    if (showFavorites && !favorites.includes(animal.id)) {
      return false;
    }

    // Text search
    const matchesSearch = searchTerm === '' || 
      animal.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      animal.breed.toLowerCase().includes(searchTerm.toLowerCase()) ||
      animal.species.toLowerCase().includes(searchTerm.toLowerCase()) ||
      animal.story.toLowerCase().includes(searchTerm.toLowerCase());

    // Filter by species (multiple selection)
    const matchesSpecies = filters.species.length === 0 || 
      filters.species.some(species => animal.species.toLowerCase() === species.toLowerCase());

    // Filter by sex (multiple selection)
    const matchesSex = filters.sex.length === 0 || 
      filters.sex.some(sex => animal.sex.toLowerCase() === sex.toLowerCase());

    // Filter by tags (multiple selection - check if ALL selected tags are in animal's tags)
    const matchesTags = filters.tags.length === 0 || 
      filters.tags.every(tag => animal.tags.toLowerCase().includes(tag.toLowerCase()));

    return matchesSearch && matchesSpecies && matchesSex && matchesTags;
  });

  const handleAnimalClick = (animal: Animal, photoIndex: number) => {
    console.log(`📋 Gallery received: ${animal.name} with photo index ${photoIndex}`);
    // Save current scroll position
    sessionStorage.setItem('animalGalleryScrollPosition', window.scrollY.toString());
    // Push state to history for back button support
    window.history.pushState({ modal: true, animalId: animal.id }, '', window.location.href);
    setSelectedAnimal(animal);
    setSelectedPhotoIndex(photoIndex);
  };

  const handleCloseModal = () => {
    setSelectedAnimal(null);
    // Go back in history if we pushed a state
    if (window.history.state?.modal) {
      window.history.back();
    }
    // Restore scroll position
    const savedPosition = sessionStorage.getItem('animalGalleryScrollPosition');
    if (savedPosition) {
      setTimeout(() => {
        window.scrollTo(0, parseInt(savedPosition, 10));
      }, 0);
    }
  };

  const handleFilterChange = (filterType: string, value: string, checked: boolean) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: checked 
        ? [...prev[filterType as keyof typeof prev], value]
        : prev[filterType as keyof typeof prev].filter(item => item !== value)
    }));
  };

  const clearFilters = () => {
    setFilters({
      species: [],
      sex: [],
      tags: []
    });
    setSearchTerm('');
  };

  const toggleFilterSection = (section: string) => {
    setExpandedFilters(prev => ({
      ...prev,
      [section]: !prev[section as keyof typeof prev]
    }));
  };

  const handleFilterToggle = () => {
    setShowFilters(!showFilters);
  };

  const handleFavoritesToggle = () => {
    setShowFavorites(!showFavorites);
  };

  const toggleFavorite = (animalId: string) => {
    setFavorites(prev => {
      if (prev.includes(animalId)) {
        return prev.filter(id => id !== animalId);
      } else {
        return [...prev, animalId];
      }
    });
  };

  // Get unique values for filter options
  const uniqueSpecies = Array.from(new Set(animals.map(animal => animal.species).filter(Boolean)));
  const uniqueSex = Array.from(new Set(animals.map(animal => animal.sex).filter(Boolean)));
  const uniqueTags = Array.from(new Set(
    animals.flatMap(animal => 
      animal.tags.split(',').map(tag => tag.trim()).filter(Boolean)
    )
  ));


  if (loading) {
    return (
      <div className="gallery-loading">
        <div className="loading-spinner"></div>
        <p>Loading our furry friends...</p>
      </div>
    );
  }

  return (
    <div className="animal-gallery">
      <header className="gallery-header">
        <h1 className="gallery-title">
          <img 
            src={process.env.PUBLIC_URL + "/pawprint.svg"} 
            alt="Pawprint" 
            className="title-paw-icon" 
            width="55" 
            height="55"
          />
          St. Augustine Shelter
        </h1>
        <p className="gallery-subtitle">Helping furry hearts find loving homes</p>
        
        <div className="gallery-search">
          <input
            type="text"
            placeholder="Try an animal or breed..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          <div className="search-buttons">
            <button 
              onClick={handleFilterToggle}
              className="filter-icon-button"
              title="Filter animals"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <path d="M3 17v2h6v-2H3zM3 5v2h10V5H3zm10 16v-2h8v-2h-8v-2h-2v6h2zM7 9v2H3v2h4v2h2V9H7zm14 4v-2H11v2h10zm-6-4h2V7h4V5h-4V3h-2v6z"/>
              </svg>
            </button>
            <button 
              onClick={handleFavoritesToggle}
              className={`favorites-icon-button ${showFavorites ? 'active' : ''}`}
              title={showFavorites ? 'Show all animals' : 'Show favorites only'}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill={showFavorites ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
              </svg>
              {favorites.length > 0 && (
                <span className="favorites-count">{favorites.length}</span>
              )}
            </button>
          </div>
        </div>

        <div className={`filter-panel-container ${showFilters ? 'expanded' : 'collapsed'}`}>
          <div className="filter-panel">
            <div className="accordion">
              {/* Species Accordion */}
              <div className="accordion-item">
                <button 
                  className="accordion-header"
                  onClick={() => toggleFilterSection('species')}
                >
                  <span>Species {filters.species.length > 0 && `(${filters.species.length})`}</span>
                  <span className={`accordion-arrow ${expandedFilters.species ? 'expanded' : ''}`}>▼</span>
                </button>
                <div className={`accordion-content ${expandedFilters.species ? 'expanded' : 'collapsed'}`}>
                  <div className="filter-options">
                    {uniqueSpecies.map(species => (
                      <label key={species} className="filter-checkbox">
                        <input
                          type="checkbox"
                          checked={filters.species.includes(species)}
                          onChange={(e) => handleFilterChange('species', species, e.target.checked)}
                        />
                        <span className="checkmark"></span>
                        {species}
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              {/* Sex Accordion */}
              <div className="accordion-item">
                <button 
                  className="accordion-header"
                  onClick={() => toggleFilterSection('sex')}
                >
                  <span>Sex {filters.sex.length > 0 && `(${filters.sex.length})`}</span>
                  <span className={`accordion-arrow ${expandedFilters.sex ? 'expanded' : ''}`}>▼</span>
                </button>
                <div className={`accordion-content ${expandedFilters.sex ? 'expanded' : 'collapsed'}`}>
                  <div className="filter-options">
                    {uniqueSex.map(sex => (
                      <label key={sex} className="filter-checkbox">
                        <input
                          type="checkbox"
                          checked={filters.sex.includes(sex)}
                          onChange={(e) => handleFilterChange('sex', sex, e.target.checked)}
                        />
                        <span className="checkmark"></span>
                        {sex}
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              {/* Features Accordion */}
              <div className="accordion-item">
                <button 
                  className="accordion-header"
                  onClick={() => toggleFilterSection('features')}
                >
                  <span>Features {filters.tags.length > 0 && `(${filters.tags.length})`}</span>
                  <span className={`accordion-arrow ${expandedFilters.features ? 'expanded' : ''}`}>▼</span>
                </button>
                <div className={`accordion-content ${expandedFilters.features ? 'expanded' : 'collapsed'}`}>
                  <div className="filter-options">
                    {uniqueTags.map(tag => (
                      <label key={tag} className="filter-checkbox">
                        <input
                          type="checkbox"
                          checked={filters.tags.includes(tag)}
                          onChange={(e) => handleFilterChange('tags', tag, e.target.checked)}
                        />
                        <span className="checkmark"></span>
                        {tag}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="filter-actions">
              <button onClick={clearFilters} className="clear-filters-button">
                Clear All
              </button>
              <div className="filter-results">
                Showing {filteredAnimals.length} of {animals.length} animals
              </div>
            </div>
          </div>
        </div>
      </header>

      {filteredAnimals.length === 0 ? (
        <div className="no-results">
          <p>No animals found matching your search.</p>
        </div>
      ) : (
        <div className="gallery-grid">
          {filteredAnimals.map((animal) => (
            <AnimalCard
              key={animal.id}
              animal={animal}
              onClick={handleAnimalClick}
              isFavorite={favorites.includes(animal.id)}
              onToggleFavorite={toggleFavorite}
            />
          ))}
        </div>
      )}

      {selectedAnimal && (
        <AnimalModal
          animal={selectedAnimal}
          onClose={handleCloseModal}
          isFavorite={favorites.includes(selectedAnimal.id)}
          onToggleFavorite={toggleFavorite}
          initialPhotoIndex={selectedPhotoIndex}
        />
      )}
    </div>
  );
};

export default AnimalGallery;